import React, { useState, useEffect } from 'react';
import { 
  View, 
  Text, 
  TextInput, 
  StyleSheet, 
  TouchableOpacity, 
  ScrollView, 
  Platform, 
  ActivityIndicator,
  Alert,
  Image,
  KeyboardAvoidingView
} from 'react-native';
import { router } from 'expo-router';
import { observer } from '@legendapp/state/react';
import { Ionicons } from '@expo/vector-icons';
import { AppAuthLoginLogger } from '../../utils/logger';
import theme from '../../config/theme';
import { logout } from '../../services/authService';
import { APP_CONSTANTS } from '../../config/constants';
import { supabase } from '../../services/supabase';
import { databaseService } from '../../services/databaseService';
import { globalState, CompanyProfile, companyActions } from '../../state/globalState';
import { useWebAlert } from '../../components/WebAlert';
import FormFieldError from '../../components/FormFieldError';
import WebLoadingOverlay from '../../components/WebLoadingOverlay';
import * as ImagePicker from 'expo-image-picker';
import { useActionSheet } from '@expo/react-native-action-sheet';
import { uploadCompanyLogo } from '../../services/userService';
import { v4 as uuid } from 'uuid';
import FormattedInput from '../../components/FormattedInput';
import cep from 'cep-promise';
import { debugPause } from '@/utils/debugPause';
import { activateTrialForCompany, createCustomer } from '../../services/subscriptionService';
import { ChecklistTemplateService } from '../../services/checklist/ChecklistTemplateService';
import { pickImageFromLibrary, takePhotoFromCamera } from '@/utils/media/imagePicker';
import { isIOS } from '@/utils/platform';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { getAndroidActionSheetSafeAreaStyle } from '@/utils/safeArea';

// Logger específico para esta tela
const logger = AppAuthLoginLogger.Register;

// Erros do formulário
interface FormErrors {
  nome: string | null;
  cnpj: string | null;
  email: string | null;
  telefone: string | null;
  endereco: string | null;
  cep: string | null;
  cidade: string | null;
  uf: string | null;
  general: string | null;
}

const RegisterScreen = observer(() => {
  // Estado do usuário atual
  const userId = globalState.user.user.get()?.id;
  const userEmail = globalState.user.user.get()?.email;
  
  // Estado de formulário da empresa
  const [companyData, setCompanyData] = useState<Partial<CompanyProfile>>({
    nome: '',
    cnpj: '',
    email: '',
    telefone: '',
    endereco: '',
    cep: '',
    cidade: '',
    uf: ''
  });
  
  // Estado de erros do formulário
  const [formErrors, setFormErrors] = useState<FormErrors>({
    nome: null,
    cnpj: null,
    email: null,
    telefone: null,
    endereco: null,
    cep: null,
    cidade: null,
    uf: null,
    general: null
  });
  
  // Estados para controle do componente
  const [isLoading, setIsLoading] = useState(false);
  const [logoUri, setLogoUri] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const { showAlert } = useWebAlert();
  const { showActionSheetWithOptions } = useActionSheet();
  const safeAreaInsets = useSafeAreaInsets();

  const requiresCompany = APP_CONSTANTS.REGISTRATION.REQUIRES_COMPANY;

  // Função para mostrar alerta adaptável à plataforma
  const showMessage = (title: string, msg: string) => {
    if (Platform.OS !== 'android') {
      // No web e no iOS, usar o alerta temático do app
      showAlert(title, msg, [{ text: 'OK' }]);
    } else {
      // No Android, manter o Alert nativo
      Alert.alert(title, msg);
    }
  };

  // Validações de campos
  const validateNome = (value?: string): boolean => {
    if (!value?.trim()) {
      setFormErrors(prev => ({ ...prev, nome: 'Nome da empresa é obrigatório' }));
      return false;
    }
    setFormErrors(prev => ({ ...prev, nome: null }));
    return true;
  };

  const validateEmail = (value?: string): boolean => {
    if (value?.trim() && !/\S+@\S+\.\S+/.test(value)) {
      setFormErrors(prev => ({ ...prev, email: 'Email inválido' }));
      return false;
    }
    setFormErrors(prev => ({ ...prev, email: null }));
    return true;
  };

  const validateCNPJ = (value?: string): boolean => {
    if (value?.trim() && !/^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/.test(value)) {
      setFormErrors(prev => ({ ...prev, cnpj: 'CNPJ deve estar no formato XX.XXX.XXX/XXXX-XX' }));
      return false;
    }
    setFormErrors(prev => ({ ...prev, cnpj: null }));
    return true;
  };
  
  const validateCEP = (value?: string): boolean => {
    if (value?.trim() && !/^\d{5}-\d{3}$/.test(value)) {
      setFormErrors(prev => ({ ...prev, cep: 'CEP deve estar no formato XXXXX-XXX' }));
      return false;
    }
    setFormErrors(prev => ({ ...prev, cep: null }));
    return true;
  };

  const validateUF = (value?: string): boolean => {
    if (value?.trim() && value.length !== 2) {
      setFormErrors(prev => ({ ...prev, uf: 'UF deve ter 2 letras' }));
      return false;
    }
    setFormErrors(prev => ({ ...prev, uf: null }));
    return true;
  };

  const validateForm = (): boolean => {
    const isNomeValid = validateNome(companyData.nome);
    const isEmailValid = validateEmail(companyData.email);
    const isCnpjValid = validateCNPJ(companyData.cnpj);
    const isCepValid = validateCEP(companyData.cep);
    const isUfValid = validateUF(companyData.uf);
    
    return isNomeValid && isEmailValid && isCnpjValid && isCepValid && isUfValid;
  };

  // Função para selecionar imagem do logo
  const handlePickImage = () => {
    showActionSheetWithOptions(
      {
        options: ['Cancelar', 'Tirar foto', 'Galeria'],
        cancelButtonIndex: 0,
        title: 'Selecionar imagem',
        message: 'De onde você quer obter o logo da empresa?',
        titleTextStyle: {
          color: theme.colors.text.primary,
          fontSize: theme.fontSizes.md,
          fontWeight: theme.fontWeights.medium as any,
        },
        messageTextStyle: {
          color: theme.colors.text.secondary,
          fontSize: theme.fontSizes.sm,
        },
        containerStyle: {
          backgroundColor: theme.colors.background.primary,
          ...getAndroidActionSheetSafeAreaStyle({
            bottomInset: safeAreaInsets.bottom,
          }),
        },
        textStyle: {
          color: theme.colors.primary.default,
          fontSize: theme.fontSizes.md,
        },
      },
      async (buttonIndex) => {
        if (buttonIndex === 1) {
          // câmera
          await takePhoto();
        }
        if (buttonIndex === 2) {
          // galeria
          await pickFromLibrary();
        }
      }
    );
  };
  
  const takePhoto = async () => {
    try {
      const outcome = await takePhotoFromCamera({
        mediaTypes: ['images'],
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.7,
      }, {
        source: 'auth_register_company_logo',
        screen: 'auth/register',
        action: 'take_company_logo_with_camera',
        entityType: 'company_logo',
        entityId: null,
        userId: userId ?? null,
      });

      if (outcome.status === 'denied') {
        showMessage('Permissão negada', 'Precisamos de permissão para acessar a câmera.');
        return;
      }

      if (outcome.status === 'failed') {
        logger.error('Erro ao tirar foto:', outcome.error);
        showMessage('Erro', 'Não foi possível tirar a foto.');
        return;
      }

      if (outcome.status === 'success' && outcome.result.assets?.length > 0) {
        const selectedImage = outcome.result.assets[0];
        setLogoUri(selectedImage.uri);
      }
    } catch (error) {
      logger.error('Erro ao tirar foto:', error);
      showMessage('Erro', 'Não foi possível tirar a foto.');
    }
  };
  
  const pickFromLibrary = async () => {
    try {
      const outcome = await pickImageFromLibrary({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.7,
      }, {
        source: 'auth_register_company_logo',
        screen: 'auth/register',
        action: 'pick_company_logo_from_library',
        entityType: 'company_logo',
        entityId: null,
        userId: userId ?? null,
      });

      if (outcome.status === 'denied') {
        showMessage('Permissão negada', 'Precisamos de permissão para acessar suas fotos.');
        return;
      }

      if (outcome.status === 'failed') {
        logger.error('Erro ao selecionar imagem da galeria:', outcome.error);
        showMessage('Erro', 'Não foi possível selecionar a imagem.');
        return;
      }

      if (outcome.status !== 'success' || !outcome.result.assets?.length) {
        return;
      }

      const selectedImage = outcome.result.assets[0];
      setLogoUri(selectedImage.uri);
    } catch (error) {
      logger.error('Erro ao selecionar imagem da galeria:', error);
      showMessage('Erro', 'Não foi possível selecionar a imagem.');
    }
  };

  // Função para registrar a empresa
  const handleRegisterCompany = async () => {
    if (!validateForm()) {
      setFormErrors(prev => ({ ...prev, general: 'Por favor, corrija os erros no formulário.' }));
      return;
    }

    if (!userId) {
      logger.error('ID de usuário não encontrado');
      showMessage('Erro', 'Usuário não identificado. Por favor, faça login novamente.');
      return;
    }

    setIsLoading(true);
    setFormErrors(prev => ({ ...prev, general: null }));

    try {
      // Gerar um ID para a empresa
      const companyId = uuid();
      
      // Preparar dados da empresa
      const companyPayload: {
        id: string;
        nome: string;
        cnpj: string | null;
        email: string | null;
        telefone: string | null;
        endereco: string | null;
        cep: string | null;
        cidade: string | null;
        uf: string | null;
        plano: string | null;
        created_at: string;
        updated_at: string;
        stripe_customer_id: string | null;
        num_fotos: number;
      } = {
        id: companyId,
        nome: companyData.nome || '',
        cnpj: companyData.cnpj || null,
        email: companyData.email || null,
        telefone: companyData.telefone || null,
        endereco: companyData.endereco || null,
        cep: companyData.cep || null,
        cidade: companyData.cidade || null,
        uf: companyData.uf || null,
        plano: 'free',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        stripe_customer_id: null,
        num_fotos: companyData.num_fotos || 5
      };      

      

      // 1. Criar empresa no banco de dados local e no Supabase
      logger.info('Registrando empresa no banco local e no Supabase');
      
      
      // Inserir no Supabase
      const { data: createdCompany, error: companyError } = await supabase
        .from(APP_CONSTANTS.DATABASE.TABLES.EMPRESAS.NAME)
        .insert(companyPayload)
        .select()
        .single();

      if (companyError) {
        logger.error('Erro ao registrar empresa no Supabase:', companyError);
        throw new Error(`Erro ao registrar empresa: ${companyError.message}`);
      }

      // 4. Preparar a empresa no Stripe independentemente da plataforma de
      // cadastro. No iOS, isso nao altera a interface: a compra continua sendo
      // oferecida somente pela App Store, mas o cliente pode assinar pela web.
      try {
        logger.info('Criando Stripe Customer...');
        // Use o email/nome do usuário, e passe o ID da empresa se desejar vincular
        const stripeResult = await createCustomer(userEmail || '', {
          empresaId: companyId
        });
        logger.info('Stripe Customer criado:', stripeResult.customerId);

        // Atualizar o payload da empresa com o stripe_customer_id
        companyPayload.stripe_customer_id = stripeResult.customerId;

      } catch (stripeErr) {
        logger.error('Erro ao criar Stripe Customer:', stripeErr);
        // Opcional: showMessage('Aviso', 'Ocorreu um problema ao criar o Stripe Customer. Entre em contato com o suporte.');
      }

      // print do company payloda aqui
      logger.info('companyPayload:', companyPayload);      

      // Inserir no banco local
      const localSuccess = await databaseService.createItem(
        APP_CONSTANTS.DATABASE.TABLES.EMPRESAS.NAME,
        companyPayload,
        true // skipPendingOperation = true
      );

      if (!localSuccess) {
        logger.warn('Falha ao inserir empresa no banco local, continuando...');
      }

      // Ativar o teste gratuito em todas as plataformas. No iOS, a interface
      // direciona para a App Store ao fim do trial; na web, Stripe permanece
      // disponivel para a mesma conta.
      try {
        logger.info('Ativando período de teste gratuito para a nova empresa...');
        const trialResult = await activateTrialForCompany({ empresaId: companyId });
        const planoAtual = trialResult.contract?.planType ?? APP_CONSTANTS.PLANS.TRIAL;
        const localUpdateSuccess = await databaseService.updateItem(
          APP_CONSTANTS.DATABASE.TABLES.EMPRESAS.NAME,
          companyId,
          { plano: planoAtual },
          true
        );

        if (!localUpdateSuccess) {
          logger.warn('Não foi possível atualizar o plano da empresa no banco local após ativar o trial.');
        }

        companyPayload.plano = planoAtual;
        logger.info('Trial criado com sucesso', { planoAtual, trialEndsAt: trialResult.trialEndsAt });
      } catch (trialError) {
        logger.error('Erro ao ativar trial gratuito:', trialError);
        showMessage(
          'Aviso',
          'Empresa criada, mas não foi possível ativar o teste gratuito automaticamente. Você pode tentar novamente na tela de Assinatura.'
        );
      }

      // === CRIAR TEMPLATE PADRÃO DE CHECKLIST APÓS CRIAR EMPRESA ===
      try {
        logger.info('Verificando existência de template padrão para a empresa antes de criar...');
        const existingTemplates = await databaseService.listItems(
          APP_CONSTANTS.DATABASE.TABLES.TEMPLATES_CHECKLIST.NAME,
          { empresa_id: companyId }
        );
        if (!existingTemplates || existingTemplates.length === 0) {
          logger.info('Nenhum template encontrado, criando template padrão de checklist...');
          await new ChecklistTemplateService().createDefaultVehicleTemplateForBootstrap(companyId, 5);
          logger.info('Template padrão de checklist criado para a nova empresa');
        } else {
          logger.info('Template padrão já existe para esta empresa, não será criado novamente');
        }
      } catch (templateError) {
        logger.error('Erro ao criar template padrão de checklist:', templateError);
      }
      // === FIM CRIAÇÃO TEMPLATE PADRÃO ===

      // 2. Fazer upload do logo se existir
      let logoUploadResult = null;
      if (logoUri) {
        logger.info('Logo encontrada, iniciando upload');
        setUploadingLogo(true);
        try {
          logger.info(`Iniciando upload do logo para empresa ${companyId}`);
          logoUploadResult = await uploadCompanyLogo(companyId, logoUri);
          if (!logoUploadResult.success) {
            throw new Error(logoUploadResult.error);
          }
          logger.info('Upload do logo concluído com sucesso');
        } catch (logoError) {
          logger.error('Erro no upload do logo:', logoError);
          showMessage('Aviso', 'O cadastro foi concluído, mas houve um problema ao fazer upload do logo. Você pode adicioná-lo mais tarde.');
        } finally {
          setUploadingLogo(false);
        }
      } else {
        logger.info('Não há logo para upload');
      }

      
      
      // Atualizar estado global da empresa
      companyActions.setCompanyData({
        ...companyPayload,
        logo_url: logoUploadResult?.success ? logoUploadResult.data?.publicURL : undefined,        
      } as CompanyProfile);      

      // 3. Concluir registro
      showMessage('Sucesso', 'Empresa registrada com sucesso!');

      logger.info('Cadastro de empresa concluído; preparando redirecionamento para /(auth)/register-user');

      // Redirecionar para o placeholder de cadastro de usuário
      setTimeout(() => {
        // Criamos um tempo curto para permitir que o alerta seja visto
        // Força uma nova renderização para mostrar a tela de cadastro de usuário
        logger.info('Navegando para /(auth)/register-user');
        router.replace('/(auth)/register-user');
      }, 1500);
    } catch (error) {
      logger.error('Erro ao registrar empresa:', error);
      
      let errorMessage = 'Ocorreu um erro ao registrar a empresa.';
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      setFormErrors(prev => ({ ...prev, general: errorMessage }));
      showMessage('Erro', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    logger.debug('Fazendo logout a partir da tela de registro de empresa');
    await logout();
    logger.info('Logout concluído; redirecionando usuário para /(auth)/login');
    router.replace('/(auth)/login');
  };

  const handleInputChange = (field: keyof FormErrors, value: string) => {
    setCompanyData(prev => ({ ...prev, [field]: value }));
    
    // Validar campo específico conforme alterado
    switch (field) {
      case 'nome':
        validateNome(value);
        break;
      case 'email':
        validateEmail(value);
        break;
      case 'cnpj':
        validateCNPJ(value);
        break;
      case 'cep':
        validateCEP(value);
        break;
      case 'uf':
        validateUF(value);
        break;
    }
  };

  // Renderização condicional com base em requiresCompany
  if (!requiresCompany) {
    return (
      <View style={styles.container}>
        <Text style={styles.title}>Registro Necessário</Text>
        <Text style={styles.subtitle}>Você precisa completar seu perfil de usuário</Text>
        
        <View style={styles.content}>
          <Text style={styles.message}>
            A tela de cadastro de usuário será implementada em breve.
          </Text>
          
          <TouchableOpacity style={styles.button} onPress={handleSignOut}>
            <Text style={styles.buttonText}>Sair</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }
  
  return (
    <KeyboardAvoidingView 
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={Platform.OS === 'ios' ? 64 : 0}
    >
      <WebLoadingOverlay
        visible={isLoading}
        message="Registrando empresa..."
        hint="Por favor, aguarde enquanto finalizamos essa etapa. Esse processo pode levar alguns instantes, então permaneça nesta tela até concluirmos."
      />
      
      <ScrollView 
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.header}>
          <Text style={styles.title}>Cadastrar Empresa</Text>
          <Text style={styles.subtitle}>
            Complete os dados da sua empresa para continuar
          </Text>
        </View>

        
        {/* Logo da empresa */}
        <View style={styles.logoContainer}>
          <TouchableOpacity 
            style={styles.logoWrapper}
            onPress={handlePickImage}
            disabled={uploadingLogo}
          >
            {logoUri ? (
              <Image source={{ uri: logoUri }} style={styles.logoImage} />
            ) : (
              <View style={styles.logoPlaceholder}>
                <Ionicons name="business" size={50} color={theme.colors.text.inverse} />
              </View>
            )}
            {uploadingLogo && (
              <View style={styles.uploadingOverlay}>
                <ActivityIndicator size="large" color={theme.colors.primary.default} />
              </View>
            )}
          </TouchableOpacity>
          <Text style={styles.logoText}>Logo da Empresa (opcional)</Text>
          <TouchableOpacity 
            style={styles.logoButton}
            onPress={handlePickImage}
            disabled={uploadingLogo}
          >
            <Text style={styles.logoButtonText}>
              {logoUri ? 'Alterar Logo' : 'Adicionar Logo'}
            </Text>
          </TouchableOpacity>
        </View>
        
        {/* Formulário */}
        <View style={styles.formContainer}>
          <Text style={styles.formSectionTitle}>Informações Básicas</Text>
          
          {/* Nome da Empresa - Campo obrigatório */}
          <View style={styles.inputContainer}>
            <Text style={styles.label}>Nome da Empresa *</Text>
            <TextInput
              style={[
                styles.input,
                formErrors.nome ? styles.inputError : null
              ]}
              placeholder="Digite o nome da empresa"
              placeholderTextColor={theme.colors.text.tertiary}
              value={companyData.nome}
              onChangeText={(text) => handleInputChange('nome', text)}
            />
            <FormFieldError message={formErrors.nome} />
          </View>
          
          {/* CNPJ */}
          <View style={styles.inputContainer}>
            <Text style={styles.label}>CNPJ</Text>
            <FormattedInput
              type="cnpj"
              style={[
                styles.input,
                formErrors.cnpj ? styles.inputError : null
              ]}
              placeholder="Digite apenas números"
              placeholderTextColor={theme.colors.text.tertiary}
              value={companyData.cnpj || ''}
              onChangeText={(text) => handleInputChange('cnpj', text)}
            />
            <FormFieldError message={formErrors.cnpj} />
          </View>
          
          {/* Email */}
          <View style={styles.inputContainer}>
            <Text style={styles.label}>Email</Text>
            <TextInput
              style={[
                styles.input,
                formErrors.email ? styles.inputError : null
              ]}
              placeholder="contato@empresa.com"
              placeholderTextColor={theme.colors.text.tertiary}
              value={companyData.email}
              onChangeText={(text) => handleInputChange('email', text)}
              keyboardType="email-address"
              autoCapitalize="none"
            />
            <FormFieldError message={formErrors.email} />
          </View>
          
          {/* Telefone */}
          <View style={styles.inputContainer}>
            <Text style={styles.label}>Telefone</Text>
            <FormattedInput
              type="phone"
              style={styles.input}
              placeholder="Digite apenas números"
              placeholderTextColor={theme.colors.text.tertiary}
              value={companyData.telefone || ''}
              onChangeText={(text) => handleInputChange('telefone', text)}
            />
          </View>
          
          <Text style={[styles.formSectionTitle, {marginTop: 16}]}>Localização</Text>
          
          {/* CEP */}
          <View style={styles.inputContainer}>
            <Text style={styles.label}>CEP</Text>
            <FormattedInput
              type="cep"
              style={[
                styles.input,
                formErrors.cep ? styles.inputError : null
              ]}
              placeholder="00000-000"
              placeholderTextColor={theme.colors.text.tertiary}
              value={companyData.cep || ''}
              onChangeText={async (text) => {
                handleInputChange('cep', text);
                // Disparar busca automática ao atingir 9 caracteres (máscara 00000-000)
                const digits = text.replace(/\D/g, '');
                if (digits.length === 8) {
                  setIsLoading(true);
                  try {
                    const result = await Promise.race([
                      cep(digits, { timeout: 3000 }),
                      new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout na busca do CEP')), 3000))
                    ]);
                    // Preencher cidade, uf e endereco
                    if (result && typeof result === 'object') {
                      const address = result as { city?: string; state?: string; street?: string };
                      handleInputChange('cidade', address.city || '');
                      handleInputChange('uf', address.state || '');
                      handleInputChange('endereco', address.street || '');
                      setFormErrors(prev => ({ ...prev, cep: null }));
                    }
                  } catch (err: any) {
                    logger.error('Erro ao buscar CEP:', err);
                    setFormErrors(prev => ({ ...prev, cep: 'CEP não encontrado. Preencha manualmente.' }));
                    showMessage('CEP não encontrado', 'Não foi possível encontrar o endereço para o CEP informado. Preencha manualmente.');
                  } finally {
                    setIsLoading(false);
                  }
                }
              }}
            />
            <FormFieldError message={formErrors.cep} />
          </View>
          
          {/* Cidade e UF - na mesma linha */}
          <View style={styles.rowContainer}>
            <View style={[styles.inputContainer, { flex: 3, marginRight: 8 }]}>
              <Text style={styles.label}>Cidade</Text>
              <TextInput
                style={styles.input}
                placeholder="Cidade"
                placeholderTextColor={theme.colors.text.tertiary}
                value={companyData.cidade}
                onChangeText={(text) => handleInputChange('cidade', text)}
              />
            </View>
            
            <View style={[styles.inputContainer, { flex: 1 }]}>
              <Text style={styles.label}>UF</Text>
              <TextInput
                style={[
                  styles.input,
                  formErrors.uf ? styles.inputError : null
                ]}
                placeholder="SP"
                placeholderTextColor={theme.colors.text.tertiary}
                value={companyData.uf}
                onChangeText={(text) => handleInputChange('uf', text.toUpperCase())}
                maxLength={2}
                autoCapitalize="characters"
              />
              <FormFieldError message={formErrors.uf} />
            </View>
          </View>
          
          {/* Endereço */}
          <View style={styles.inputContainer}>
            <Text style={styles.label}>Endereço</Text>
            <TextInput
              style={styles.input}
              placeholder="Rua, número, complemento"
              placeholderTextColor={theme.colors.text.tertiary}
              value={companyData.endereco}
              onChangeText={(text) => handleInputChange('endereco', text)}
            />
          </View>
          
          {/* Erro geral do formulário */}
          {formErrors.general && (
            <Text style={styles.generalError}>{formErrors.general}</Text>
          )}
          
          {/* Botões de ação */}
          <View style={styles.buttonContainer}>
            <TouchableOpacity 
              style={[styles.secondaryButton]} 
              onPress={handleSignOut}
              disabled={isLoading}
            >
              <Text style={styles.secondaryButtonText}>Sair</Text>
            </TouchableOpacity>
            
            <TouchableOpacity 
              style={[styles.primaryButton, isLoading ? styles.disabledButton : null]} 
              onPress={handleRegisterCompany}
              disabled={isLoading}
            >
              {isLoading ? (
                <ActivityIndicator size="small" color={theme.colors.text.inverse} />
              ) : (
                <Text style={styles.primaryButtonText}>Registrar Empresa</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.secondary,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: theme.spacing.xl,
  },
  header: {
    paddingHorizontal: theme.spacing.lg,
    paddingTop: Platform.OS === 'ios' ? theme.spacing.xl + theme.spacing.md : theme.spacing.xl,
    paddingBottom: theme.spacing.lg,
    backgroundColor: theme.colors.primary.default,
  },
  title: {
    fontSize: theme.fontSizes.xxl,
    fontWeight: theme.fontWeights.bold as any,
    color: theme.colors.text.inverse,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.inverse,
    textAlign: 'center',
    marginTop: theme.spacing.xs,
    opacity: 0.9,
  },
  content: {
    padding: theme.spacing.lg,
    alignItems: 'center',
  },
  message: {
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.secondary,
    textAlign: 'center',
    marginBottom: theme.spacing.lg,
  },
  logoContainer: {
    alignItems: 'center',
    paddingVertical: theme.spacing.lg,
    backgroundColor: theme.colors.background.primary,
    marginBottom: theme.spacing.md,
  },
  logoWrapper: {
    width: 120,
    height: 120,
    borderRadius: 10,
    overflow: 'hidden',
    marginBottom: theme.spacing.sm,
  },
  logoImage: {
    width: '100%',
    height: '100%',
  },
  logoPlaceholder: {
    width: '100%',
    height: '100%',
    backgroundColor: theme.colors.primary.light,
    justifyContent: 'center',
    alignItems: 'center',
  },
  uploadingOverlay: {
    ...StyleSheet.absoluteFill,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  logoText: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.secondary,
    marginBottom: theme.spacing.sm,
  },
  logoButton: {
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.xs,
    borderWidth: 1,
    borderColor: theme.colors.primary.default,
    borderRadius: theme.borderRadius.sm,
  },
  logoButtonText: {
    color: theme.colors.primary.default,
    fontSize: theme.fontSizes.sm,
    fontWeight: theme.fontWeights.medium as any,
  },
  formContainer: {
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    margin: theme.spacing.md,
    ...theme.shadows.md,
  },
  formSectionTitle: {
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.semibold as any,
    color: theme.colors.text.primary,
    marginBottom: theme.spacing.sm,
    marginTop: theme.spacing.xs,
  },
  inputContainer: {
    marginBottom: theme.spacing.md,
  },
  label: {
    fontSize: theme.fontSizes.sm,
    fontWeight: theme.fontWeights.medium as any,
    color: theme.colors.text.secondary,
    marginBottom: theme.spacing.xs,
  },
  input: {
    backgroundColor: theme.colors.background.secondary,
    borderWidth: 1,
    borderColor: theme.colors.border.default,
    borderRadius: theme.borderRadius.sm,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: Platform.OS === 'ios' ? theme.spacing.sm : theme.spacing.xs,
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.primary,
  },
  inputError: {
    borderColor: theme.colors.util.error,
  },
  rowContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  generalError: {
    color: theme.colors.util.error,
    fontSize: theme.fontSizes.sm,
    textAlign: 'center',
    marginVertical: theme.spacing.sm,
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: theme.spacing.lg,
  },
  primaryButton: {
    backgroundColor: theme.colors.primary.default,
    borderRadius: theme.borderRadius.sm,
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    alignItems: 'center',
    justifyContent: 'center',
    flex: 3,
  },
  secondaryButton: {
    borderColor: theme.colors.border.default,
    borderWidth: 1,
    borderRadius: theme.borderRadius.sm,
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.md,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing.md,
    flex: 1,
  },
  disabledButton: {
    opacity: 0.7,
  },
  primaryButtonText: {
    color: theme.colors.text.inverse,
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.medium as any,
  },
  secondaryButtonText: {
    color: theme.colors.text.secondary,
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.medium as any,
  },
  button: {
    backgroundColor: theme.colors.primary.default,
    borderRadius: 8,
    padding: 16,
    alignItems: 'center',
    marginTop: 8,
    width: '60%',
  },
  buttonText: {
    color: theme.colors.text.inverse,
    fontSize: 16,
    fontWeight: 'bold',
  },
});

export default RegisterScreen; 
